/**
 * One feed at a time (Kyle, 2026-10-02): "We only want one feed up at a time … when the local weather
 * feed is down, it just pulls from awos or metar for the field it's at."
 *
 * The default order is the dropzone's own equipment first, then the field's AWOS read on site, then the
 * field's airport report from the national feed. The first source that is up wins. "Up" means it is not
 * `not_reporting` and has a reading: a LATE source still wins (it is shown with its "Late" badge, because
 * a few minutes behind on the hangar's own anemometer beats the airport 20 minutes away), but a
 * NOT-REPORTING one hands over to the next.
 *
 * Handing over is always said on screen ("Hangar anemometer has not reported since 14:05: showing the
 * field's airport weather"). A fallback that happened silently would make an airport reading look like
 * the hangar's own.
 */

import type { SourceReading } from './types.js'

/** Lower is preferred. Kinds that are not live surface observations are never a feed. */
const KIND_RANK: Record<string, number> = {
  'davis-local': 0,
  tempest: 0,
  'push-ecowitt': 0,
  'push-wunderground': 0,
  ambient: 1,
  'davis-cloud': 1,
  'awos-bridge': 2,
  'noaa-metar': 3,
  manual: 4,
}
const NOT_A_FEED = new Set(['noaa-taf', 'nws-forecast', 'noaa-windtemp'])

export function feedRank(kind: string): number {
  return KIND_RANK[kind] ?? 5
}

export const isUp = (r: SourceReading): boolean => r.health !== 'not_reporting' && r.observation !== null

export interface FeedChoice {
  /** The one feed on screen, or null when the site has no surface sources at all. */
  shown: SourceReading | null
  /** The site's preferred feed (its own equipment, when it has some). */
  preferred: SourceReading | null
  /** True when `shown` is not the preferred feed because the preferred one is down. */
  fallback: boolean
  /** True when nothing is up and `shown` is the best stale reading available. */
  allDown: boolean
}

export function chooseFeed(readings: SourceReading[]): FeedChoice {
  const feeds = readings
    .filter((r) => !NOT_A_FEED.has(r.descriptor.kind))
    .map((r, i) => ({ r, i }))
    .sort((a, b) => feedRank(a.r.descriptor.kind) - feedRank(b.r.descriptor.kind) || a.i - b.i)
    .map(({ r }) => r)
  const preferred = feeds[0] ?? null
  const up = feeds.find(isUp) ?? null
  if (up) return { shown: up, preferred, fallback: up !== preferred, allDown: false }
  // Nothing is up: show the preferred source's last reading, greyed and badged, rather than nothing.
  const lastKnown = feeds.find((r) => r.observation !== null) ?? preferred
  return { shown: lastKnown, preferred, fallback: lastKnown !== preferred, allDown: feeds.length > 0 }
}
